import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash, ArrowRight } from '@phosphor-icons/react';
import { useCart } from '../context/CartContext';
import api from '../utils/api';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (cart?.items?.length > 0) {
      fetchProductDetails();
    } else {
      setLoading(false);
    }
  }, [cart]);

  const fetchProductDetails = async () => {
    try {
      const productIds = cart.items.map(item => item.product_id);
      const productPromises = productIds.map(id => api.get(`/products/${id}`));
      const responses = await Promise.all(productPromises);
      setProducts(responses.map(r => r.data));
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCartItemWithProduct = (item) => {
    const product = products.find(p => p.id === item.product_id);
    return { ...item, product };
  };

  const calculateTotal = () => {
    return cart.items.reduce((sum, item) => {
      const product = products.find(p => p.id === item.product_id);
      return sum + (product ? product.price * item.quantity : 0);
    }, 0);
  };

  const handleRemove = async (productId) => {
    await removeFromCart(productId);
    toast.success('Item removed from cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="loading-spinner" data-testid="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-7xl mx-auto px-6">
        <h1 data-testid="cart-page-heading" className="text-4xl md:text-5xl font-light tracking-tighter mb-12">
          Shopping Cart
        </h1>

        {!cart?.items || cart.items.length === 0 ? (
          <div data-testid="empty-cart-message" className="text-center py-24">
            <p className="text-[#7a5e75] mb-8">Your cart is empty</p>
            <Link to="/products" data-testid="continue-shopping-button" className="btn-primary inline-flex items-center gap-2">
              Continue Shopping
              <ArrowRight size={20} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cart.items.map(item => {
                const itemWithProduct = getCartItemWithProduct(item);
                const { product } = itemWithProduct;
                if (!product) return null;

                return (
                  <div
                    key={item.product_id}
                    data-testid={`cart-item-${item.product_id}`}
                    className="bg-white border border-[#E5E2DC] p-6 flex gap-6"
                  >
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-24 h-24 object-cover"
                    />
                    <div className="flex-1">
                      <h3 data-testid={`cart-item-name-${item.product_id}`} className="text-xl font-light mb-2">
                        {product.name}
                      </h3>
                      {item.customization?.finish && (
                        <p className="text-sm text-[#7a5e75] mb-2">
                          Finish: {item.customization.finish}
                        </p>
                      )}
                      <p data-testid={`cart-item-price-${item.product_id}`} className="text-lg font-semibold text-[#641b4d]">
                        ₹{product.price.toFixed(2)} × {item.quantity}
                      </p>
                    </div>
                    <button
                      data-testid={`remove-item-${item.product_id}`}
                      onClick={() => handleRemove(item.product_id)}
                      className="text-[#bc64aa] hover:text-[#8c1666] transition-colors"
                    >
                      <Trash size={24} weight="light" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div data-testid="order-summary" className="bg-white border border-[#E5E2DC] p-8 sticky top-24">
                <h2 className="text-2xl font-light mb-6">Order Summary</h2>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-[#7a5e75]">Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-semibold">₹{calculateTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7a5e75]">Shipping</span>
                    <span className="font-semibold">Calculated at checkout</span>
                  </div>
                  <div className="border-t border-[#E5E2DC] pt-4 flex justify-between text-xl">
                    <span className="font-semibold">Total</span>
                    <span data-testid="cart-total" className="font-semibold">₹{calculateTotal().toFixed(2)}</span>
                  </div>
                </div>
                <button
                  data-testid="proceed-to-checkout-button"
                  onClick={() => navigate('/checkout')}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  Proceed to Checkout
                  <ArrowRight size={20} weight="bold" />
                </button>
                <Link
                  to="/products"
                  data-testid="continue-shopping-link"
                  className="btn-secondary w-full mt-4 block text-center"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;